import { useEffect } from 'react';

interface AnalyticsTrackerProps {
    sessionId: string;
    pageUrl: string;
    pageTitle: string;
    trackEvent: (params: {
        eventType: string;
        eventName: string;
        elementId?: string;
        elementClass?: string;
        metadata?: Record<string, any>;
    }) => Promise<void>;
}

export const AnalyticsTracker: React.FC<AnalyticsTrackerProps> = ({
    trackEvent,
}) => {
    useEffect(() => {
        // Track all button clicks
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const button = target.closest('button');
            const link = target.closest('a');

            if (button) {
                trackEvent({
                    eventType: 'button_click',
                    eventName: button.textContent?.trim() || button.id || button.className || 'unknown_button',
                    elementId: button.id,
                    elementClass: button.className,
                });
                return;
            }

            // Track link clicks (only when not a button click)
            if (link) {
                trackEvent({
                    eventType: 'link_click',
                    eventName: link.textContent?.trim() || link.href || 'unknown_link',
                    elementId: link.id,
                    elementClass: link.className,
                    metadata: { href: link.href },
                });
            }
        };

        // Track form submissions
        const handleSubmit = (e: Event) => {
            const form = e.target as HTMLFormElement;
            trackEvent({
                eventType: 'form_submit',
                eventName: form.id || form.name || form.className || 'unknown_form',
                elementId: form.id,
                elementClass: form.className,
            });
        };

        // Track form field focus
        const handleFocus = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
                trackEvent({
                    eventType: 'form_field_focus',
                    eventName: (target as HTMLInputElement).name || target.id || 'unknown_field',
                    elementId: target.id,
                    elementClass: target.className,
                });
            }
        };

        // Track form field blur
        const handleBlur = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
                trackEvent({
                    eventType: 'form_field_blur',
                    eventName: (target as HTMLInputElement).name || target.id || 'unknown_field',
                    elementId: target.id,
                    elementClass: target.className,
                });
            }
        };

        // Use capture phase for click/submit to catch all submissions
        document.addEventListener('click', handleClick, true);
        document.addEventListener('submit', handleSubmit, true);
        // Use focusin/focusout so focus changes bubble up from inputs
        document.addEventListener('focusin', handleFocus);
        document.addEventListener('focusout', handleBlur);

        return () => {
            document.removeEventListener('click', handleClick, true);
            document.removeEventListener('submit', handleSubmit, true);
            document.removeEventListener('focusin', handleFocus);
            document.removeEventListener('focusout', handleBlur);
        };
    }, [trackEvent]);

    return null; // This component doesn't render anything
};
